import { useEffect, useState, useCallback } from "react";

// Module-level cache so multiple consumers on the same page share one request
let cachedPaymentTypes = null;
let paymentTypesPromise = null;

const FALLBACK_PAYMENT_TYPES = [
	{ value: "2", label: "Credit Card", type: "card", type_display: "Credit Card" },
	{ value: "cash", label: "Cash", type: "cash", type_display: "Cash" },
	{ value: "debit_card", label: "Debit Card", type: "card", type_display: "Debit Card" },
	{ value: "bank_transfer", label: "Bank Transfer", type: "bank", type_display: "Bank Transfer" },
	{ value: "mobile_payment", label: "Mobile Payment", type: "mobile", type_display: "Mobile Payment" },
	{ value: "check", label: "Check", type: "check", type_display: "Check" }
];

export const usePaymentTypes = () => {
	const [paymentTypes, setPaymentTypes] = useState(cachedPaymentTypes || []);
	const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(!cachedPaymentTypes);
	const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

	const fetchPaymentTypes = useCallback(async (force = false) => {
		if (!force) {
			if (cachedPaymentTypes) {
				setPaymentTypes(cachedPaymentTypes);
				setLoadingPaymentTypes(false);
				return cachedPaymentTypes;
			}
			if (paymentTypesPromise) {
				// Another instance is already fetching — wait for it, then sync
				// THIS instance's state too, or its loading spinner never ends.
				const shared = await paymentTypesPromise;
				const result = cachedPaymentTypes || shared;
				setPaymentTypes(result);
				setLoadingPaymentTypes(false);
				return result;
			}
		}

		setLoadingPaymentTypes(true);
		paymentTypesPromise = (async () => {
			try {
				const token = localStorage.getItem("access_token");
				const response = await fetch(`${API_BASE_URL}/api/v1/payment-types`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					}
				});

				const data = await response.json();

				if (data?.success && Array.isArray(data.data)) {
					const transformed = data.data.map((payment) => ({
						value: String(payment.id),
						label: payment.name,
						type: payment.type,
						type_display: payment.type_display
					}));
					cachedPaymentTypes = transformed;
					return transformed;
				}

				cachedPaymentTypes = FALLBACK_PAYMENT_TYPES;
				return FALLBACK_PAYMENT_TYPES;
			} catch {
				cachedPaymentTypes = FALLBACK_PAYMENT_TYPES;
				return FALLBACK_PAYMENT_TYPES;
			}
		})();

		try {
			const result = await paymentTypesPromise;
			setPaymentTypes(result);
			return result;
		} finally {
			paymentTypesPromise = null;
			setLoadingPaymentTypes(false);
		}
	}, [API_BASE_URL]);

	useEffect(() => {
		fetchPaymentTypes();
	}, [fetchPaymentTypes]);

	return { paymentTypes, loadingPaymentTypes, reloadPaymentTypes: () => fetchPaymentTypes(true) };
};
